import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  ApiResponse,
  PromoCodePayload,
  TableFilterParams,
} from "interface/promoCode";
import type { StatusInterface } from "interface/common";

const getFormattedParams = (params: TableFilterParams = {}) => {
  const normalizeFilterValue = (value?: string | string[] | null): string => {
    if (Array.isArray(value)) return value[0] ?? "";
    return value ?? "";
  };
  const normalizeMultiValue = (value?: Array<string | boolean> | null): string => {
    if (Array.isArray(value)) return value.length ? value.join("-") : "";
    return "";
  };

  const formatted: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.pageSize ?? 10,
  };

  const title = normalizeFilterValue(params.title);
  const code = normalizeFilterValue(params.code);
  const status = normalizeMultiValue(params.status);
  const startDate = normalizeFilterValue(params.start_date);
  const endDate = normalizeFilterValue(params.end_date);
  const updatedAt = normalizeFilterValue(params.updatedAt);

  if (title) formatted.title = title;
  if (code) formatted.code = code;
  if (status) formatted.status = status;
  if (startDate) formatted.start_date = startDate;
  if (endDate) formatted.end_date = endDate;
  if (updatedAt) formatted.updatedAt = updatedAt;

  if (params.sortField) {
    formatted.sort = params.sortField;
    if (params.sortOrder) {
      formatted.direction = params.sortOrder === "ascend" ? "asc" : "desc";
    }
  }

  return formatted;
};

export const promoCodeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPromoCodes: builder.query<ApiResponse, TableFilterParams>({
      query: (params) => ({
        url: API.GET_PROMO_CODE,
        method: "GET",
        params: getFormattedParams(params),
      }),
      providesTags: ["PromoCode"],
    }),
    createPromoCode: builder.mutation<ApiResponse, PromoCodePayload>({
      query: ({ params }) => ({
        url: API.GET_PROMO_CODE,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["PromoCode"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create promo code:", error);
        }
      },
    }),
    updatePromoCode: builder.mutation<ApiResponse, PromoCodePayload>({
      query: ({ params }) => ({
        url: API.GET_PROMO_CODE,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: ["PromoCode"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to update promo code:", error);
        }
      },
    }),
    updatePromoCodeStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...body }) => ({
        url: `/${API.GET_PROMO_CODE}/status/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PromoCode"],
    }),
    bulkUpdatePromoCodeStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_PROMO_CODE}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["PromoCode"],
    }),
    deletePromoCode: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_PROMO_CODE}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PromoCode"],
    }),
    bulkDeletePromoCodes: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_PROMO_CODE}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["PromoCode"],
    }),
  }),
});

export const {
  useGetPromoCodesQuery,
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useUpdatePromoCodeStatusMutation,
  useDeletePromoCodeMutation,
  useBulkUpdatePromoCodeStatusMutation,
  useBulkDeletePromoCodesMutation,
} = promoCodeApi;
